import XhrFileReader from 'jsmediatags/build2/XhrFileReader';
import readAudioProperties, { AudioProperties, ByteSource } from '../../utilities/audioProperties';
import XhrFileReaderWithLength from './XhrFileReaderWithLength';

/**
 * readAudioProperties over a file at a url, through the same range-request
 * reader jsmediatags uses for the tags - so it works wherever the tag read
 * does, on the same download url, and reads in the same 1 KB chunks.
 *
 * Shaped like jsmediatags.read - `(url, { onSuccess, onError })` - so the
 * tag queue can run it as it runs a tag read.
 */

export interface AudioPropertiesCallbacks {
  /** Nothing, for a file whose bytes are not a format the parser reads. */
  onSuccess(properties: AudioProperties | undefined): void;
  onError?(error: unknown): void;
}

function readerSource(reader: XhrFileReader): ByteSource {
  return {
    get size() {
      return reader.getSize();
    },
    load(start, end) {
      return new Promise<void>((resolve, reject) => {
        reader.loadRange([start, Math.min(end, reader.getSize() - 1)], {
          onSuccess: resolve,
          onError: reject,
        });
      });
    },
    byteAt: (offset) => reader.getByteAt(offset),
  };
}

function audioPropertiesReader(Reader: typeof XhrFileReader) {
  return function read(url: string, callbacks: AudioPropertiesCallbacks) {
    const reader = new Reader(url);

    reader.init({
      onSuccess() {
        readAudioProperties(readerSource(reader)).then(callbacks.onSuccess, callbacks.onError);
      },
      onError: callbacks.onError,
    });
  };
}

/** For a url the reader may size itself: a HEAD request, or a ranged GET. */
export const readAudioPropertiesFromUrl = audioPropertiesReader(XhrFileReader);

/**
 * For a url whose size is already in XhrFileReaderWithLength.urlToSizeMap -
 * the OneDrive download url, which the tag read seeds the same way.
 */
export const readAudioPropertiesWithLength = audioPropertiesReader(XhrFileReaderWithLength);
